<template>
  <v-dialog
    v-model="dialog"
    max-width="1040px"
    :fullscreen="$vuetify.breakpoint.smAndDown"
    scrollable
  >
    <v-card class="install-queue-card">
      <div class="install-queue-header primary white--text">
        <div class="install-queue-header__icon">
          <v-icon color="white">
            mdi-tray-arrow-down
          </v-icon>
        </div>
        <div class="install-queue-header__text">
          <div class="install-queue-header__title">
            Queue App Installation
          </div>
          <div class="install-queue-header__subtitle">
            Push a Scripts Downloads APK to a limited number of devices within a scheduled window.
          </div>
        </div>
        <v-btn
          icon
          dark
          class="install-queue-header__close"
          @click="dialog = false"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-card-text class="install-queue-body">
        <v-container class="pa-0">
          <!-- Step 1: App + APK -->
          <div class="install-queue-section pa-5 mb-4">
            <div class="install-queue-step-title mb-4">
              <span class="install-queue-step-num">1</span>
              Choose app &amp; APK
            </div>
            <v-row dense>
              <v-col
                cols="12"
                md="6"
              >
                <v-autocomplete
                  v-model="selectedAppId"
                  :items="appOptions"
                  item-text="displayLabel"
                  item-value="id"
                  label="Company app"
                  placeholder="Select an app"
                  :loading="loadingApps"
                  :disabled="loadingApps || submitting"
                  hide-details
                  @change="onAppSelected"
                />
              </v-col>
              <v-col
                cols="12"
                md="6"
              >
                <v-select
                  v-model="selectedApkId"
                  :items="matchingApkOptions"
                  item-text="displayLabel"
                  item-value="id"
                  label="Available APK"
                  :placeholder="apkPlaceholder"
                  :loading="loadingApks"
                  :disabled="loadingApks || submitting || !selectedAppId || !matchingApkOptions.length"
                  hide-details
                />
              </v-col>
            </v-row>
            <div
              v-if="selectedApp"
              class="text-body-2 mt-3 grey--text text--darken-2"
            >
              Package: {{ selectedApp.packageName || 'n/a' }}
            </div>
            <v-alert
              v-if="apkLoadMessage"
              dense
              text
              type="warning"
              class="mb-0 mt-3"
            >
              {{ apkLoadMessage }}
            </v-alert>
          </div>

          <!-- Step 2: Count + schedule window -->
          <div class="install-queue-section pa-5 mb-4">
            <div class="install-queue-step-title mb-4">
              <span class="install-queue-step-num">2</span>
              Set limit &amp; schedule
            </div>
            <v-row dense>
              <v-col
                cols="12"
                md="4"
              >
                <v-text-field
                  v-model.number="maximumInstallationCount"
                  type="number"
                  min="1"
                  label="Maximum installation count"
                  hint="Max devices that may install"
                  persistent-hint
                  :disabled="submitting"
                  class="install-queue-count-field"
                >
                  <template v-slot:prepend>
                    <v-btn
                      icon
                      small
                      color="primary"
                      :disabled="submitting || Number(maximumInstallationCount) <= 1"
                      @click="stepCount(-1)"
                    >
                      <v-icon>mdi-minus-circle</v-icon>
                    </v-btn>
                  </template>
                  <template v-slot:append-outer>
                    <v-btn
                      icon
                      small
                      color="primary"
                      :disabled="submitting"
                      @click="stepCount(1)"
                    >
                      <v-icon>mdi-plus-circle</v-icon>
                    </v-btn>
                  </template>
                </v-text-field>
              </v-col>
              <v-col
                cols="12"
                md="4"
              >
                <v-text-field
                  v-model="installStartAt"
                  type="datetime-local"
                  label="Install starts at"
                  hide-details
                  :disabled="submitting"
                />
              </v-col>
              <v-col
                cols="12"
                md="4"
              >
                <v-text-field
                  v-model="installEndAt"
                  type="datetime-local"
                  label="Install ends at"
                  hide-details
                  :disabled="submitting"
                />
              </v-col>
            </v-row>
          </div>

          <!-- Step 3: Devices (optional) -->
          <div class="install-queue-section pa-5">
            <div class="d-flex align-center flex-wrap mb-2">
              <div class="install-queue-step-title">
                <span class="install-queue-step-num">3</span>
                Select devices
                <span class="install-queue-optional">(optional)</span>
              </div>
              <v-spacer />
              <v-chip
                label
                :color="selectedDevices.length ? 'primary' : 'grey'"
                text-color="white"
                class="font-weight-medium install-queue-count-chip"
              >
                <v-icon
                  small
                  left
                >
                  mdi-cellphone-check
                </v-icon>
                {{ selectedDevices.length ? selectedDevices.length + ' selected' : 'All devices' }}
              </v-chip>
            </div>

            <v-alert
              dense
              text
              type="info"
              class="mb-4"
            >
              Leave devices unselected to target <strong>all available devices</strong> in this company. Either way, at most <strong>{{ maxCountLabel }}</strong> device{{ Number(maximumInstallationCount) === 1 ? '' : 's' }} will install (per the limit above).
            </v-alert>

            <v-text-field
              v-model="deviceSearch"
              append-icon="mdi-magnify"
              label="Search devices"
              outlined
              single-line
              hide-details
              clearable
              class="mb-4 install-queue-search"
              style="max-width: 360px"
            />

            <v-data-table
              v-model="selectedDevices"
              :headers="deviceHeaders"
              :items="filteredDevices"
              :loading="loadingDevices"
              item-key="id"
              show-select
              class="install-queue-device-table"
              :items-per-page="5"
              :footer-props="{ 'items-per-page-options': [5, 10, 25] }"
            >
              <template v-slot:[`header.data-table-select`]="{ }">
                <v-simple-checkbox
                  color="primary"
                  :value="allFilteredSelected"
                  :indeterminate="someFilteredSelected"
                  :ripple="false"
                  @input="toggleSelectAllFiltered"
                />
              </template>
              <template v-slot:[`item.name`]="{ item }">
                <span
                  class="font-weight-medium"
                  :class="{ 'grey--text text--lighten-1 font-italic': !item.name }"
                >
                  {{ item.name || 'Unnamed device' }}
                </span>
              </template>
              <template v-slot:[`item.os`]="{ item }">
                <v-chip
                  v-if="item.os"
                  small
                  outlined
                  color="teal"
                >
                  <v-icon
                    small
                    left
                  >
                    mdi-android
                  </v-icon>
                  {{ item.os }}
                </v-chip>
                <span
                  v-else
                  class="grey--text text--lighten-1"
                >&mdash;</span>
              </template>
              <template v-slot:[`item.macAddress`]="{ item }">
                <span class="install-queue-device-id">{{ item.macAddress || '-' }}</span>
              </template>
            </v-data-table>
          </div>
        </v-container>
      </v-card-text>

      <v-divider />
      <v-card-actions class="install-queue-actions px-4 py-3">
        <div class="text-caption grey--text text--darken-1">
          <span v-if="selectedApk">{{ selectedApk.displayLabel }}</span>
        </div>
        <v-spacer />
        <v-btn
          text
          :disabled="submitting"
          @click="dialog = false"
        >
          Cancel
        </v-btn>
        <app-btn
          :disabled="!canSubmit"
          :loading="submitting"
          @click="submit"
        >
          Queue Installation
        </app-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
  import defaultMyDeviceIcon from '@/assets/logo.png'

  const toastOptions = { position: 'topRight' }
  const ANDROID_PACKAGE_FILE_PATTERN = /\.(apk|apkm)(?=$|[?#\s])/i
  const MYDEVICE_PACKAGE_NAME = 'au.com.softclient.mydevices'
  const DEFAULT_MYDEVICE_APP_ID = '__default_mydevice_app__'
  const DEFAULT_MYDEVICE_APP = {
    id: DEFAULT_MYDEVICE_APP_ID,
    icon: '',
    title: 'MyDevice',
    label: 'MyDevice',
    type: 'Default',
    autoLaunch: false,
    vpnConnect: false,
    visible: true,
    packageName: MYDEVICE_PACKAGE_NAME,
    activity: '',
    __defaultMyDeviceApp: true,
  }

  export default {
    name: 'AppInstallationQueueDialog',
    props: {
      value: {
        type: Boolean,
        default: false,
      },
      selectedCompanyId: {
        type: [Number, String],
        default: 0,
      },
    },
    data () {
      return {
        dialog: this.value,
        loadingApps: false,
        appOptions: [],
        selectedAppId: null,
        loadingApks: false,
        apkOptions: [],
        apkLoadError: '',
        selectedApkId: null,
        maximumInstallationCount: 1,
        installStartAt: '',
        installEndAt: '',
        loadingDevices: false,
        devices: [],
        selectedDevices: [],
        deviceSearch: '',
        submitting: false,
        deviceHeaders: [
          { text: 'Name', value: 'name' },
          { text: 'Model', value: 'deviceModel' },
          { text: 'OS / Android', value: 'os' },
          { text: 'Device Id', value: 'macAddress' },
        ],
      }
    },
    computed: {
      selectedApp () {
        return this.appOptions.find(app => app.id === this.selectedAppId) || null
      },
      matchingApkOptions () {
        const packageName = this.normalizePackage(this.selectedApp?.packageName)
        if (!packageName) {
          return []
        }

        return this.apkOptions.filter(
          apk => this.normalizePackage(apk.packageName) === packageName,
        )
      },
      selectedApk () {
        return this.matchingApkOptions.find(apk => apk.id === this.selectedApkId) || null
      },
      apkPlaceholder () {
        if (!this.selectedAppId) {
          return 'Select an app first'
        }
        return this.matchingApkOptions.length ? 'Select an APK' : 'APK not found'
      },
      apkLoadMessage () {
        if (!this.selectedAppId || this.loadingApks) {
          return ''
        }
        if (this.matchingApkOptions.length) {
          return ''
        }
        if (this.apkLoadError) {
          return `APK not found: ${this.apkLoadError}`
        }
        return `APK not found: no APK in Scripts Downloads matched ${this.selectedApp?.packageName || 'this app'} yet.`
      },
      filteredDevices () {
        const query = String(this.deviceSearch || '').trim().toLowerCase()
        if (!query) {
          return this.devices
        }

        return this.devices.filter(device =>
          [device.name, device.deviceModel, device.macAddress]
            .filter(Boolean)
            .some(value => String(value).toLowerCase().includes(query)),
        )
      },
      selectedDeviceIds () {
        return new Set(this.selectedDevices.map(device => device.id))
      },
      allFilteredSelected () {
        return this.filteredDevices.length > 0 &&
          this.filteredDevices.every(device => this.selectedDeviceIds.has(device.id))
      },
      someFilteredSelected () {
        return !this.allFilteredSelected &&
          this.filteredDevices.some(device => this.selectedDeviceIds.has(device.id))
      },
      maxCountLabel () {
        const count = Number(this.maximumInstallationCount)
        return count > 0 ? count : '—'
      },
      canSubmit () {
        return !this.submitting &&
          !!this.selectedCompanyId &&
          !!this.selectedAppId &&
          !!this.selectedApk &&
          Number(this.maximumInstallationCount) > 0 &&
          !!this.installStartAt &&
          !!this.installEndAt
      },
    },
    watch: {
      value (val) {
        this.dialog = val
        if (val) {
          this.onOpen()
        }
      },
      dialog (val) {
        this.$emit('input', val)
      },
      selectedCompanyId () {
        if (this.dialog) {
          this.onOpen()
        }
      },
    },
    methods: {
      normalizePackage (value) {
        return String(value || '').trim().toLowerCase()
      },
      onOpen () {
        this.resetForm()
        this.loadApps()
        this.loadDevices()
      },
      resetForm () {
        this.selectedAppId = null
        this.selectedApkId = null
        this.apkOptions = []
        this.apkLoadError = ''
        this.maximumInstallationCount = 1
        this.installStartAt = this.toLocalInput(new Date())
        this.installEndAt = this.toLocalInput(new Date(Date.now() + 24 * 60 * 60 * 1000))
        this.selectedDevices = []
        this.deviceSearch = ''
      },
      toLocalInput (date) {
        // Format a Date as the value expected by <input type="datetime-local">.
        const pad = value => String(value).padStart(2, '0')
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
          `T${pad(date.getHours())}:${pad(date.getMinutes())}`
      },
      async loadApps () {
        if (!this.selectedCompanyId) {
          this.appOptions = []
          return
        }

        this.loadingApps = true
        try {
          const response = await this.$http.post(`/CompanyKioskApp/paginated/${this.selectedCompanyId}`, {
            pageNumber: 1,
            pageSize: 500,
          })

          const items = response.data?.data?.items || []
          const realApps = items
            .filter(app => this.hasNumericId(app?.id))
            .map(app => ({
              ...app,
              displayLabel: this.buildAppLabel(app),
            }))

          this.appOptions = this.includeDefaultMyDeviceApp(realApps)
        } catch (error) {
          this.appOptions = []
          this.$toast.error('Could not load company apps.', 'Error', toastOptions)
        } finally {
          this.loadingApps = false
        }
      },
      buildAppLabel (app) {
        const name = app.title || app.label || app.packageName || 'Untitled app'
        return app.packageName && app.packageName !== name
          ? `${name} (${app.packageName})`
          : name
      },
      hasNumericId (value) {
        return Number.isFinite(Number(value)) && Number(value) > 0
      },
      includeDefaultMyDeviceApp (apps) {
        const hasMyDeviceApp = apps.some(
          app => this.normalizePackage(app?.packageName) === MYDEVICE_PACKAGE_NAME,
        )
        if (hasMyDeviceApp) {
          return apps
        }

        return [
          { ...DEFAULT_MYDEVICE_APP, displayLabel: this.buildAppLabel(DEFAULT_MYDEVICE_APP) },
          ...apps,
        ]
      },
      isDefaultMyDeviceApp (app) {
        return app?.id === DEFAULT_MYDEVICE_APP_ID
      },
      async getDefaultMyDeviceIconFile () {
        const response = await fetch(defaultMyDeviceIcon)
        const blob = await response.blob()
        return new File([blob], 'mydevice.png', { type: blob.type || 'image/png' })
      },
      async createDefaultMyDeviceApp () {
        const formData = new FormData()
        formData.append('KioskApps[0].type', DEFAULT_MYDEVICE_APP.type)
        formData.append('KioskApps[0].autoLaunch', !!DEFAULT_MYDEVICE_APP.autoLaunch)
        formData.append('KioskApps[0].vpnConnect', !!DEFAULT_MYDEVICE_APP.vpnConnect)
        formData.append('KioskApps[0].visible', !!DEFAULT_MYDEVICE_APP.visible)
        formData.append('KioskApps[0].label', DEFAULT_MYDEVICE_APP.label)
        formData.append('KioskApps[0].activity', DEFAULT_MYDEVICE_APP.activity)
        formData.append('KioskApps[0].title', DEFAULT_MYDEVICE_APP.title)
        formData.append('KioskApps[0].packageName', DEFAULT_MYDEVICE_APP.packageName)
        formData.append('KioskApps[0].icon', await this.getDefaultMyDeviceIconFile())

        const response = await this.$http.post(`/CompanyKioskApp/${this.selectedCompanyId}`, formData)
        if (response?.data?.success === false) {
          throw new Error(response?.data?.message || 'Default MyDevice app registration failed.')
        }

        const data = response?.data?.data
        const createdApp = Array.isArray(data)
          ? data.find(app => this.hasNumericId(app?.id))
          : (this.hasNumericId(data?.id) ? data : null)

        if (!createdApp?.id) {
          throw new Error('Default MyDevice app was registered but no app id was returned.')
        }

        return createdApp
      },
      async resolveSelectedCompanyKioskAppId () {
        if (this.hasNumericId(this.selectedAppId)) {
          return Number(this.selectedAppId)
        }

        if (!this.isDefaultMyDeviceApp(this.selectedApp)) {
          return 0
        }

        const createdApp = await this.createDefaultMyDeviceApp()
        return Number(createdApp.id)
      },
      onAppSelected () {
        this.selectedApkId = null
        this.loadApks()
      },
      async getDeviceConfigsByCompany () {
        const response = await this.$http.get('DeviceConfig/GetDeviceConfigsByCompany', {
          params: { CompanyId: this.selectedCompanyId },
        })
        return Array.isArray(response.data) ? response.data : []
      },
      isDownloadsConfig (config) {
        const name = String(config?.name || '').trim().toLowerCase()
        const path = String(config?.devicePath || '').trim().toLowerCase().replace(/\\/g, '/')
        return /\bdownloads?\b/.test(name) ||
          path === 'download' || path === 'downloads' ||
          path.endsWith('/download') || path.endsWith('/downloads')
      },
      async loadApks () {
        if (!this.selectedAppId || !this.selectedCompanyId) {
          this.apkOptions = []
          return
        }

        this.loadingApks = true
        this.apkLoadError = ''
        try {
          const configs = await this.getDeviceConfigsByCompany()
          const downloadsConfigs = configs.filter(config => this.isDownloadsConfig(config))

          if (!downloadsConfigs.length) {
            this.apkOptions = []
            this.apkLoadError = 'No Scripts Downloads config was found for this company.'
            return
          }

          const files = (
            await Promise.all(
              downloadsConfigs.map(async (config) => {
                const response = await this.$http.get('DeviceConfig/GetFilePathsByDeviceConfigs', {
                  params: { DeviceConfigId: config.id },
                })
                return Array.isArray(response.data) ? response.data : []
              }),
            )
          ).flat()

          const seen = new Set()
          this.apkOptions = files
            .map(file => this.normalizeApkOption(file))
            .filter((apk) => {
              if (!apk || seen.has(apk.id)) {
                return false
              }
              seen.add(apk.id)
              return true
            })

          if (!this.apkOptions.length) {
            this.apkLoadError = 'No APK files were found in Scripts Downloads.'
          }
        } catch (error) {
          this.apkOptions = []
          this.apkLoadError = 'Could not load APK files from Scripts Downloads.'
        } finally {
          this.loadingApks = false
          this.syncSelectedApk()
        }
      },
      normalizeApkOption (file) {
        const name = file?.name || file?.fileName || file?.filePath || ''
        const searchSource = [name, file?.filePath, file?.path].filter(Boolean).join(' ')
        if (!ANDROID_PACKAGE_FILE_PATTERN.test(searchSource)) {
          return null
        }

        const deviceConfigFilePathId = file?.deviceConfigFilePathId || file?.filePathId || file?.id || 0
        const versionName = file?.versionName || file?.version || ''
        return {
          id: deviceConfigFilePathId || name,
          deviceConfigFilePathId,
          name,
          packageName: file?.packageName || file?.package || '',
          versionName,
          displayLabel: versionName ? `${versionName} - ${name}` : name,
        }
      },
      syncSelectedApk () {
        const hasSelected = this.matchingApkOptions.some(apk => apk.id === this.selectedApkId)
        if (!hasSelected) {
          this.selectedApkId = this.matchingApkOptions[0]?.id || null
        }
      },
      async loadDevices () {
        if (!this.selectedCompanyId) {
          this.devices = []
          return
        }

        this.loadingDevices = true
        try {
          const response = await this.$http.get('Company/DevicePaginationByCompany', {
            params: {
              companyId: this.selectedCompanyId,
              page: 1,
              limit: 1000,
            },
          })
          this.devices = (Array.isArray(response.data) ? response.data : [])
            .filter(device => this.hasNumericId(device?.id))
        } catch (error) {
          this.devices = []
          this.$toast.error('Could not load devices.', 'Error', toastOptions)
        } finally {
          this.loadingDevices = false
        }
      },
      stepCount (delta) {
        const current = Number(this.maximumInstallationCount) || 0
        this.maximumInstallationCount = Math.max(1, current + delta)
      },
      toggleSelectAllFiltered (value) {
        // Select/clear every device across all pages that matches the current search.
        if (value) {
          const existing = this.selectedDeviceIds
          const additions = this.filteredDevices.filter(device => !existing.has(device.id))
          this.selectedDevices = [...this.selectedDevices, ...additions]
        } else {
          const filteredIds = new Set(this.filteredDevices.map(device => device.id))
          this.selectedDevices = this.selectedDevices.filter(device => !filteredIds.has(device.id))
        }
      },
      async submit () {
        if (!this.canSubmit) {
          return
        }

        this.submitting = true
        try {
          const companyKioskAppId = await this.resolveSelectedCompanyKioskAppId()
          if (!companyKioskAppId) {
            this.$toast.error('Could not resolve the company app to install.', 'Error', toastOptions)
            return
          }

          // No devices picked -> send null so the backend targets all available
          // devices in the company (still capped by maximumInstallationCount).
          const hasDeviceSelection = this.selectedDevices.length > 0
          const macAddresses = this.selectedDevices
            .map(device => device.macAddress)
            .filter(Boolean)

          const payload = {
            companyId: Number(this.selectedCompanyId),
            companyKioskAppId,
            deviceConfigFilePathId: Number(this.selectedApk.deviceConfigFilePathId),
            maximumInstallationCount: Number(this.maximumInstallationCount),
            installStartAt: new Date(this.installStartAt).toISOString(),
            installEndAt: new Date(this.installEndAt).toISOString(),
            silentInstall: true,
            forceInstall: true,
            deviceIds: hasDeviceSelection ? this.selectedDevices.map(device => Number(device.id)) : null,
            macAddresses: hasDeviceSelection && macAddresses.length ? macAddresses : null,
          }

          const response = await this.$http.post('AppInstallation/Queue', payload)
          if (response?.data?.success === false) {
            this.$toast.error(response.data.message || 'Failed to queue installation.', 'Error', toastOptions)
            return
          }

          this.$toast.success('Installation queued successfully.', 'Success', toastOptions)
          this.$emit('queued', payload)
          this.dialog = false
        } catch (error) {
          this.$toast.error(
            error?.response?.data?.message || error?.message || 'Failed to queue installation.',
            'Error',
            toastOptions,
          )
        } finally {
          this.submitting = false
        }
      },
    },
  }
</script>

<style scoped>
  .install-queue-card {
    display: flex;
    flex-direction: column;
    max-height: 88vh;
    overflow: hidden;
  }

  /* Fullscreen (mobile): let the card fill the viewport. */
  .v-dialog--fullscreen .install-queue-card {
    max-height: 100vh;
    height: 100vh;
  }

  .install-queue-header {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 14px 16px;
  }

  .install-queue-header__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 46px;
    height: 46px;
    flex: 0 0 auto;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.18);
  }

  .install-queue-header__icon >>> .v-icon {
    font-size: 28px;
  }

  .install-queue-header__text {
    min-width: 0;
    flex: 1 1 auto;
  }

  .install-queue-header__title {
    font-size: 1.35rem;
    font-weight: 600;
    line-height: 1.3;
    letter-spacing: normal;
    text-transform: none;
  }

  .install-queue-header__subtitle {
    color: rgba(255, 255, 255, 0.88);
    font-size: 0.9rem;
    line-height: 1.35;
    letter-spacing: normal;
    text-transform: none;
    white-space: normal;
    margin-top: 3px;
  }

  .install-queue-header__close {
    flex: 0 0 auto;
    align-self: flex-start;
  }

  .install-queue-body {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    background: #fafafa;
    padding-top: 20px;
  }

  .install-queue-section {
    background: #ffffff;
    border: 1px solid #ececec;
    border-radius: 12px;
  }

  .install-queue-step-title {
    display: flex;
    align-items: center;
    font-size: 1.15rem;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.85);
  }

  .install-queue-step-num {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    margin-right: 12px;
    border-radius: 50%;
    background: var(--v-primary-base, #4caf50);
    color: #fff;
    font-size: 0.95rem;
    font-weight: 700;
  }

  .install-queue-count-chip {
    font-size: 0.95rem;
    height: 34px;
  }

  .install-queue-optional {
    margin-left: 8px;
    font-size: 0.85rem;
    font-weight: 400;
    color: rgba(0, 0, 0, 0.5);
  }

  /* Larger field text across the dialog. */
  .install-queue-body >>> .v-label {
    font-size: 1.05rem;
  }

  .install-queue-body >>> input,
  .install-queue-body >>> .v-select__selection {
    font-size: 1.1rem;
  }

  .install-queue-body >>> .v-text-field .v-input__slot {
    min-height: 46px;
  }

  /* Count stepper: big +/- buttons, hidden native spinner, large centered value. */
  .install-queue-count-field >>> input[type='number'] {
    -moz-appearance: textfield;
    text-align: center;
    font-size: 1.4rem;
    font-weight: 600;
  }

  .install-queue-count-field >>> input[type='number']::-webkit-outer-spin-button,
  .install-queue-count-field >>> input[type='number']::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  .install-queue-count-field >>> .v-input__prepend-outer,
  .install-queue-count-field >>> .v-input__append-outer {
    margin-top: 2px;
    align-self: center;
  }

  .install-queue-count-field >>> .v-icon {
    font-size: 22px;
  }

  .install-queue-actions {
    flex: 0 0 auto;
  }

  .install-queue-device-table >>> thead th {
    white-space: nowrap;
    font-size: 0.9rem;
  }

  .install-queue-device-table >>> tbody td {
    font-size: 1rem;
    height: 52px;
  }

  .install-queue-device-id {
    font-family: 'Roboto Mono', monospace;
    font-size: 0.9rem;
    color: rgba(0, 0, 0, 0.7);
  }
</style>
